import { createStyles, makeStyles } from "@material-ui/core";
import {
    AreaNotificationMessage,
    Button,
    CircleIcon,
    DesktopPage,
    DesktopPageActions,
    DesktopPageSection,
    DesktopTabbedContent,
    JobStatusLabel,
    RelodeTheme,
    Text,
} from "@relode/core";
import * as React from "react";

import { useClientPublicJobContext } from "../_infra";
import { ClientPublicJobDetailsProps } from "./clientPublicJobDetailsProps";
import { ClientPublicJobInsights } from "./clientPublicJobInsights";
import { ClientPublicJobOverview } from "./clientPublicJobOverview";
import { ClientPublicJobQualifications } from "./clientPublicJobQualifications";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            circle: {
                fontSize: "0.5em",
                color: theme.palette.action.active,
                margin: theme.spacing(0, 1, 0, 1),
            },
            subtitle: {
                display: "flex",
                alignItems: "center",
            },
        }),
    { name: "JobDetailsDesktop" }
);

export const ClientPublicJobDetailsDesktop = (
    props: ClientPublicJobDetailsProps
) => {
    const classes = useStyles(props);
    const {
        state: {
            ui,
            data: { job },
        },
    } = useClientPublicJobContext();

    if (ui.fetchState === "error") {
        return (
            <DesktopPage
                pageTitle="job not found"
                actions={
                    <DesktopPageActions
                        showLogo={true}
                        title="job not found"
                        pageActions={
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={props.onSignIn}
                            >
                                sign in
                            </Button>
                        }
                    />
                }
            >
                <div>
                    <AreaNotificationMessage level="error">
                        {ui.error.isBadRequest() ? (
                            <>{ui.error.friendlyMessages}</>
                        ) : (
                            <>
                                The information you are looking for cannot be
                                found. Contact support at{" "}
                                <a
                                    href="mailto:support@relode.com"
                                    target="_blank"
                                >
                                    support@relode.com
                                </a>{" "}
                                if you have any questions.
                            </>
                        )}
                    </AreaNotificationMessage>
                </div>
            </DesktopPage>
        );
    }

    if (!job) return null;

    return (
        <>
            <DesktopPage
                pageTitle={job.details.jobName}
                data-cy="jobDetails-page"
                //id="jobDetails"
                isBusy={ui.fetchState === "loading"}
                actions={
                    <DesktopPageActions
                        showLogo={true}
                        title={job.details ? job.details.jobName : "n/a"}
                        statusLabel={
                            job.details && job.details.jobStatus ? (
                                <JobStatusLabel
                                    title={job.details.jobStatus.description}
                                    jobStatus={job.details.jobStatus}
                                />
                            ) : null
                        }
                        subtitle={
                            <div className={classes.subtitle}>
                                <Text dataCy="locationName">
                                    {job.details.locationName}
                                </Text>
                                <CircleIcon className={classes.circle} />
                                <Text dataCy="teamName">
                                    {job.details.teamName}
                                </Text>
                            </div>
                        }
                        pageActions={
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={props.onSignIn}
                            >
                                sign in
                            </Button>
                        }
                    />
                }
            >
                <DesktopTabbedContent defaultSection="insights">
                    <DesktopPageSection label="details" id="details">
                        <ClientPublicJobOverview />
                    </DesktopPageSection>

                    <DesktopPageSection label="quals" id="quals">
                        <ClientPublicJobQualifications />
                    </DesktopPageSection>

                    <DesktopPageSection label="insights" id="insights">
                        <ClientPublicJobInsights />
                    </DesktopPageSection>
                </DesktopTabbedContent>
            </DesktopPage>
        </>
    );
};
